import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ColumnDef,
  createColumnHelper,
  VisibilityState,
} from "@tanstack/react-table";
import {
  AccessControlDoorType,
  LockResponse,
  RegisterLockRequest,
  SiteResponse,
} from "api/ApiClient";
import axios from "axios";
import {
  AdminLocksTab,
  AdminLocksTabValue,
  ProxyScope,
  ProxyScopeValue,
} from "./constants";
import { parseLastSeenTimestamp } from "utils/parseLastSeenTimestamp";
import { DataTable } from "components/shared/DataTable";
import { AdminTablePagination } from "components/shared/AdminTablePagination";
import { defaultPageSize } from "helpers";
import { Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BASE_API_URL } from "store/Me";
import {
  LOCKS_VIEW,
  setLocksViewPreference,
} from "shared/utils/locksViewPreference";
import useDebounce from "utils/useDebounce";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Alert } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Switch } from "components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";
import { statusBadgeOutlineClass, type StatusColor } from "shared/utils/statusChipColor";

const LOCKS_QUERY_KEY = "locks";

// Columns beyond this default set are still selectable via the "Columns" menu, just hidden
// until the user opts in (applies only until they've customized visibility for this table).
const lockColumnVisibility: VisibilityState = {
  id: false,
  externalId: false,
  deviceType: false,
  operatingMode: false,
  noTourId: false,
  noTourEnabled: false,
  readerSerialNumber: false,
  firmwareLock: false,
  firmwareUpdateVersion: false,
  auditInvalidCredentials: false,
  firmwareMain: false,
  firmwareBLE2: false,
  firmwareMainBl: false,
  firmwareMacAdr: false,
  firmwareEthernetMacAdr: false,
  configSyncTimestamp: false,
  auditSyncTimestamp: false,
  databaseSyncTimestamp: false,
  cachedChildDevices: false,
};

const lockColumnHelper = createColumnHelper<LockResponse>();
const proxyColumnHelper = createColumnHelper<SiteProxyListItem>();

export default function AdminLocks({
  search,
  setSearch,
  page,
  setPage,
  pageSize,
  setPageSize,
  tab,
  setTab,
  proxyScope,
  setProxyScope,
  isSuperUser,
  currentTenantName,
  doorType,
  setDoorType,
  siteId,
  setSiteId,
}: {
  search: string;
  setSearch: (search: string) => void;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  tab: AdminLocksTabValue;
  setTab: (tab: AdminLocksTabValue) => void;
  proxyScope: ProxyScopeValue;
  setProxyScope: (scope: ProxyScopeValue) => void;
  isSuperUser: boolean;
  currentTenantName: string;
  doorType: string;
  setDoorType: (doorType: string) => void;
  siteId: string;
  setSiteId: (siteId: string) => void;
}) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const { data: sites } = useGetSites();
  const debouncedSearchTerm = useDebounce(search, 500);
  const isGatewayTab = tab === "gateways";
  const isProxyTab = tab === "proxies";
  const { data, isLoading, isError } = useGetLocks(
    debouncedSearchTerm,
    page,
    pageSize,
    doorType,
    siteId,
    tab,
    proxyScope,
  );

  const lockColumns = useMemo<ColumnDef<LockResponse, any>[]>(
    () => [
      lockColumnHelper.accessor("name", {
        header: isGatewayTab ? "Gateway Name" : "Lock Name",
        cell: (info) => (
          <Link
            to={`/admin/locks/${info.row.original.id}`}
            state={{ guest: info.row.original }}
          >
            {info.getValue()}
          </Link>
        ),
      }),
      lockColumnHelper.accessor((lock) => lock.site?.name, {
        id: "site",
        header: "Site",
      }),
      lockColumnHelper.accessor("onlineStatus", {
        header: "Status",
        cell: (info) => (
          <StatusWithLastSeen
            status={deriveLockStatus(info.getValue())}
            lastSeenTimeStamp={info.row.original.lastSeenTimeStamp}
          />
        ),
      }),
      ...(!isGatewayTab
        ? [
            lockColumnHelper.accessor("doorType", { header: "Door Type" }),
            lockColumnHelper.accessor(
              (lock) =>
                lock.parentGatewayName || lock.parentGatewaySystemLockId,
              {
                id: "parentGateway",
                header: "Parent Gateway",
                cell: (info) => {
                  const lock = info.row.original;
                  return lock.parentGatewayId ? (
                    <Link to={`/admin/locks/${lock.parentGatewayId}`}>
                      {info.getValue()}
                    </Link>
                  ) : null;
                },
              },
            ),
          ]
        : []),
      lockColumnHelper.accessor("systemLockId", {
        header: "SystemLockId",
        size: 260,
      }),
      lockColumnHelper.accessor("readerSerialNumber", {
        header: "Reader Serial Number",
      }),
      lockColumnHelper.accessor("id", { header: "ID" }),
      lockColumnHelper.accessor("externalId", { header: "External ID" }),
      lockColumnHelper.accessor("deviceType", { header: "Device Type" }),
      lockColumnHelper.accessor("operatingMode", {
        header: "Operating Mode",
      }),
      lockColumnHelper.accessor("noTourId", { header: "No-Tour ID" }),
      lockColumnHelper.accessor("noTourEnabled", {
        header: "No-Tour Enabled",
        cell: (info) => formatBoolean(info.getValue()),
      }),
      lockColumnHelper.accessor("firmwareLock", { header: "Firmware Lock" }),
      lockColumnHelper.accessor("firmwareUpdateVersion", {
        header: "Firmware Update Version",
      }),
      lockColumnHelper.accessor("auditInvalidCredentials", {
        header: "Audit Invalid Credentials",
        cell: (info) => formatBoolean(info.getValue()),
      }),
      lockColumnHelper.accessor("firmwareMain", { header: "Firmware Main" }),
      lockColumnHelper.accessor("firmwareBLE2", { header: "Firmware BLE2" }),
      lockColumnHelper.accessor("firmwareMainBl", {
        header: "Firmware Main Bootloader",
      }),
      lockColumnHelper.accessor("firmwareMacAdr", {
        header: "Firmware MAC Address",
      }),
      lockColumnHelper.accessor("firmwareEthernetMacAdr", {
        header: "Ethernet MAC Address",
      }),
      lockColumnHelper.accessor("configSyncTimestamp", {
        header: "Config Sync",
        cell: (info) => formatSyncTimestamp(info.getValue()),
      }),
      lockColumnHelper.accessor("auditSyncTimestamp", {
        header: "Audit Sync",
        cell: (info) => formatSyncTimestamp(info.getValue()),
      }),
      lockColumnHelper.accessor("databaseSyncTimestamp", {
        header: "Database Sync",
        cell: (info) => formatSyncTimestamp(info.getValue()),
      }),
      lockColumnHelper.accessor("cachedChildDevices", {
        header: "Child Devices",
        cell: (info) => info.getValue()?.length ?? "-",
      }),
      lockColumnHelper.display({
        id: "actions",
        header: "Actions",
        enableHiding: false,
        cell: (info) => <LockRowActions lock={info.row.original} />,
      }),
    ],
    [isGatewayTab],
  );

  const proxyColumns = useMemo<ColumnDef<SiteProxyListItem, any>[]>(
    () => [
      proxyColumnHelper.accessor("name", {
        header: "Proxy Name",
        cell: (info) => (
          <Link to={`/admin/locks/proxies/${info.row.original.id}`}>
            {info.getValue()}
          </Link>
        ),
      }),
      proxyColumnHelper.accessor("siteName", { header: "Site" }),
      proxyColumnHelper.accessor("status", {
        header: "Status",
        cell: (info) => (
          <StatusWithLastSeen
            status={info.getValue()}
            lastSeenTimeStamp={info.row.original.lastSeenTimestamp}
          />
        ),
      }),
      proxyColumnHelper.accessor("version", { header: "Version" }),
      proxyColumnHelper.accessor("softwareVersion", {
        header: "Software Version",
        cell: (info) => info.getValue() ?? "-",
      }),
    ],
    [],
  );

  return (
    <main className="space-y-6">
      <header className="pb-5 border-b border-slate-200 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold leading-7 text-slate-900">
            Locks
          </h1>
          <Link
            to="/admin/locks-legacy"
            onClick={() => setLocksViewPreference(LOCKS_VIEW.LEGACY)}
            className="inline-block mt-1 text-sm underline text-slate-500 underline-offset-2 hover:text-slate-700"
          >
            Switch to legacy view
          </Link>
        </div>
        {!isProxyTab ? (
          <Button className="mt-4 sm:mt-0" onClick={() => setAddModalOpen(true)} type="button">
            Add Lock
          </Button>
        ) : null}
      </header>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={tab}
          onValueChange={(value) => {
            setTab(value as AdminLocksTabValue);
            setPage(0);
          }}
        >
          <TabsList>
            <TabsTrigger value={AdminLocksTab.LOCKS}>Locks</TabsTrigger>
            <TabsTrigger value={AdminLocksTab.GATEWAYS}>Gateways</TabsTrigger>
            <TabsTrigger value={AdminLocksTab.PROXIES}>Proxies</TabsTrigger>
          </TabsList>
        </Tabs>
        {isProxyTab && isSuperUser ? (
          <div className="flex items-center gap-2">
            <Switch
              aria-label="Toggle proxy scope between tenant and all tenants"
              checked={proxyScope === "all"}
              id="admin-locks-proxy-scope"
              onCheckedChange={(checked) =>
                setProxyScope(checked ? "all" : "tenant")
              }
            />
            <label className="text-sm font-medium text-slate-700" htmlFor="admin-locks-proxy-scope">
              {proxyScope === "all" ? "all" : currentTenantName || "tenant"}
            </label>
          </div>
        ) : null}
      </div>
      <section
        aria-labelledby="locks-list-heading"
        className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200"
      >
        <div className="flex flex-col gap-4 px-4 py-4 border-b border-slate-200 lg:flex-row lg:items-center lg:justify-between">
          <h2 id="locks-list-heading" className="text-base font-semibold text-slate-900">
            {isGatewayTab ? "Gateways" : isProxyTab ? "Proxies" : "Locks"}
          </h2>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className="relative block w-full sm:w-64">
              <span className="sr-only">Search</span>
              <Search
                aria-hidden="true"
                className="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 size-4 text-slate-400"
              />
              <input
                className="w-full h-10 py-2 pr-3 text-sm transition-colors bg-white border rounded-md outline-none border-slate-200 pl-9 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="Search.."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <select
              aria-label="Filter by site"
              className="h-10 px-3 text-sm bg-white border rounded-md outline-none border-slate-200 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              id="siteId"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
            >
              <option value="">All Sites</option>
              {sites?.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
            {!isGatewayTab && !isProxyTab ? (
              <select
                aria-label="Filter by door type"
                className="h-10 px-3 text-sm bg-white border rounded-md outline-none border-slate-200 text-slate-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                value={doorType}
                onChange={(e) => setDoorType(e.target.value)}
              >
                <option value="">All Door Types</option>
                {Object.keys(AccessControlDoorType)
                  .filter((k) => isNaN(Number(k)))
                  .map((k, i) => (
                    <option key={i} value={k}>
                      {k}
                    </option>
                  ))}
              </select>
            ) : null}
          </div>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center gap-3 text-sm min-h-80 text-slate-500">
            Loading...
          </div>
        ) : isError ? (
          <div className="p-4">
            <Alert variant="destructive">There was an error loading data.</Alert>
          </div>
        ) : data ? (
          <div>
            {isProxyTab ? (
              <DataTable
                key="proxies"
                columns={proxyColumns}
                data={data as SiteProxyListItem[]}
                getRowId={(proxy) => proxy.id}
                emptyMessage="No proxies found."
                enableColumnVisibility
                enableColumnResizing
                enableColumnOrdering
                enableColumnPinning
                persistStateKey="admin-proxies"
                enableUrlSync
              />
            ) : (
              <DataTable
                key={tab}
                columns={lockColumns}
                data={data as LockResponse[]}
                getRowId={(lock, index) => lock.id ?? `lock-${index}`}
                emptyMessage={
                  isGatewayTab ? "No gateways found." : "No locks found."
                }
                enableColumnVisibility
                initialColumnVisibility={lockColumnVisibility}
                enableColumnResizing
                enableColumnOrdering
                enableColumnPinning
                persistStateKey={
                  isGatewayTab ? "admin-gateways" : "admin-locks"
                }
                enableUrlSync
              />
            )}
            <AdminTablePagination
              currentPage={page}
              pageSize={pageSize || defaultPageSize}
              visibleRowCount={data.length}
              isLoading={isLoading}
              disablePrevious={page === 0}
              disableNext={data.length < pageSize}
              onNext={() => setPage(page + 1)}
              onPrevious={() => setPage(Math.max(0, page - 1))}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
            />
          </div>
        ) : null}
      </section>

      <AddLockModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        sites={sites ?? []}
      />
    </main>
  );
}

// ─── Phase 2: LockRow with Delete ───────────────────────────────────────────

function LockRowActions({ lock }: { lock: LockResponse }) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { mutate: deleteLock, isPending: isDeleting } = useMutation({
    mutationFn: () => axios.delete(`${BASE_API_URL}/api/Locks/${lock.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOCKS_QUERY_KEY] });
      setConfirmOpen(false);
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "An error occurred deleting the lock.";
      setDeleteError(typeof msg === "string" ? msg : JSON.stringify(msg));
    },
  });

  return (
    <>
      <Button
        onClick={() => {
          setDeleteError(null);
          setConfirmOpen(true);
        }}
        size="sm"
        type="button"
        variant="destructive"
      >
        Delete
      </Button>

      <Dialog
        open={confirmOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setConfirmOpen(false);
          }
        }}
      >
        <DialogContent showCloseButton={false} className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle>Delete Lock</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{lock.name}</strong>? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {deleteError && <Alert variant="destructive">{deleteError}</Alert>}
          <DialogFooter>
            <Button
              disabled={isDeleting}
              onClick={() => setConfirmOpen(false)}
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={isDeleting}
              onClick={() => deleteLock()}
              type="button"
              variant="destructive"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

type SiteProxyListItem = {
  id: string;
  name: string;
  siteId: string;
  siteName: string;
  status: string;
  version: number;
  softwareVersion?: string;
  lastSeenTimestamp?: string;
};

// ─── Phase 1: AddLockModal ───────────────────────────────────────────────────

function AddLockModal({
  open,
  onClose,
  sites,
}: {
  open: boolean;
  onClose: () => void;
  sites: SiteResponse[];
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [selectedSiteId, setSelectedSiteId] = useState("");
  const [systemLockId, setSystemLockId] = useState("");
  const [selectedDoorType, setSelectedDoorType] =
    useState<AccessControlDoorType>(AccessControlDoorType.Door);
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: (payload: RegisterLockRequest) =>
      axios.post(`${BASE_API_URL}/api/Locks`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LOCKS_QUERY_KEY] });
      handleClose();
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "An error occurred adding the lock.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    },
  });

  const handleClose = () => {
    setName("");
    setSelectedSiteId("");
    setSystemLockId("");
    setSelectedDoorType(AccessControlDoorType.Door);
    setError(null);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutate({
      name,
      siteId: selectedSiteId,
      systemLockId,
      doorType: selectedDoorType,
    });
  };

  const isValid =
    name.trim().length > 0 &&
    selectedSiteId.length > 0 &&
    systemLockId.trim().length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-lg"
      >
        <form className="grid gap-4" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Lock</DialogTitle>
          </DialogHeader>
          {error && (
            <Alert variant="destructive">
              {error}
            </Alert>
          )}
          <div className="grid gap-2">
            <Label htmlFor="add-lock-name">Lock Name</Label>
            <Input
              id="add-lock-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="add-lock-site">Site</Label>
            <Select
              value={selectedSiteId}
              onValueChange={setSelectedSiteId}
            >
              <SelectTrigger id="add-lock-site" className="w-full">
                <SelectValue placeholder="Select a site" />
              </SelectTrigger>
              <SelectContent>
                {sites
                  .filter((site): site is SiteResponse & { id: string } => !!site.id)
                  .map((site) => (
                    <SelectItem key={site.id} value={site.id}>
                      {site.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="add-lock-system-id">System Lock ID</Label>
            <Input
              id="add-lock-system-id"
              required
              value={systemLockId}
              onChange={(event) => setSystemLockId(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="add-lock-door-type">Door Type</Label>
            <Select
              value={selectedDoorType}
              onValueChange={(doorType) =>
                setSelectedDoorType(doorType as AccessControlDoorType)
              }
            >
              <SelectTrigger id="add-lock-door-type" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(AccessControlDoorType).map((doorType) => (
                  <SelectItem key={doorType} value={doorType}>
                    {doorType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button disabled={isPending} onClick={handleClose} type="button" variant="outline">
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || isPending}>
              {isPending ? "Adding..." : "Add Lock"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Shared helpers ──────────────────────────────────────────────────────────

function deriveLockStatus(onlineStatus?: string | null) {
  return onlineStatus ?? null;
}

function formatBoolean(value?: boolean) {
  if (value == null) return "-";
  return value ? "Yes" : "No";
}

function formatSyncTimestamp(value?: Date) {
  const parsed = parseLastSeenTimestamp(value);
  return parsed ? parsed.toLocaleString() : "-";
}

function StatusWithLastSeen({
  status,
  lastSeenTimeStamp,
}: {
  status?: string | null;
  lastSeenTimeStamp?: Date | string;
}) {
  if (status == null || status === "") {
    return null;
  }

  const normalized = status.toString();
  const lowered = normalized.toLowerCase();

  let color: StatusColor = "default";
  if (lowered === "unknown") {
    color = "default";
  } else if (lowered === "online" || lowered === "ok") {
    color = "success";
  } else if (lowered === "warning") {
    color = "warning";
  } else if (lowered === "offline" || lowered === "error") {
    color = "error";
  }

  const isOffline = lowered === "offline";
  const lastSeenDate = parseLastSeenTimestamp(lastSeenTimeStamp);
  const hasValidLastSeen = !!lastSeenDate;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 4,
      }}
    >
      <Badge className={statusBadgeOutlineClass(color)} variant="outline">
        {normalized}
      </Badge>
      {isOffline && hasValidLastSeen ? (
        <span style={{ fontSize: 12, color: "#777" }}>
          Last seen {lastSeenDate.toLocaleString()}
        </span>
      ) : null}
    </div>
  );
}

async function getLocks(
  searchString: string,
  page: number,
  pageSize: number,
  doorType: string,
  siteId: string,
  tab: AdminLocksTabValue,
  proxyScope: ProxyScopeValue,
) {
  const urlParams = new URLSearchParams();
  urlParams.append("searchString", searchString);
  urlParams.append("page", page.toString());
  urlParams.append("pageSize", pageSize.toString());
  if (tab === AdminLocksTab.LOCKS) {
    urlParams.append("doorType", doorType);
    urlParams.append("excludedDeviceType", "gwe");
    urlParams.append("siteId", siteId);
    const url = `${BASE_API_URL}/api/locks?${urlParams.toString()}`;
    const response = await axios.get<LockResponse[]>(url);
    return response.data;
  }

  if (tab === AdminLocksTab.GATEWAYS) {
    urlParams.append("deviceType", "gwe");
    urlParams.append("siteId", siteId);
    const url = `${BASE_API_URL}/api/locks?${urlParams.toString()}`;
    const response = await axios.get<LockResponse[]>(url);
    return response.data;
  }

  if (siteId) {
    urlParams.append("siteId", siteId);
  }
  if (proxyScope === ProxyScope.ALL) {
    urlParams.append("includeAllTenants", "true");
  }
  const url = `${BASE_API_URL}/api/proxies?${urlParams.toString()}`;
  const response = await axios.get<SiteProxyListItem[]>(url);
  return response.data;
}

function useGetLocks(
  search: string,
  page: number,
  pageSize: number,
  doorType: string,
  siteId: string,
  tab: AdminLocksTabValue,
  proxyScope: ProxyScopeValue,
) {
  return useQuery({
    queryKey: [
      LOCKS_QUERY_KEY,
      tab,
      search,
      page,
      pageSize,
      doorType,
      siteId,
      proxyScope,
    ],
    queryFn: () =>
      getLocks(search, page, pageSize, doorType, siteId, tab, proxyScope),
  });
}

async function getSites() {
  const result = await axios.get<SiteResponse[]>(
    `${BASE_API_URL}/api/Sites?page=0&pageSize=100&searchString=`,
  );
  return result.data;
}

function useGetSites() {
  return useQuery({
    queryKey: ["sites"],
    queryFn: getSites,
  });
}
